import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { 
  auth,
  db
} from '../firebase';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  collection, 
  query, 
  orderBy, 
  getDocs,
  onSnapshot,
  updateDoc,
  serverTimestamp,
  setDoc,
  deleteDoc
} from 'firebase/firestore';
import api from '../services/api';

const AuthContext = createContext();

// Cache for storing token and its expiration time
const tokenCache = {
  token: null,
  expiration: null
};

// Check if we're running in development mode and using the proxy URL
const isDevelopmentProxy = process.env.NODE_ENV === 'development' && 
                          window.location.href.includes('127.0.0.1') &&
                          !window.location.href.includes('localhost');

// Add request interceptor to add Authorization header
api.interceptors.request.use(async (config) => {
  try {
    const user = auth.currentUser;
    if (user) {
      const now = Date.now();
      
      // Check if we have a cached token that's still valid (with 5-minute buffer)
      if (tokenCache.token && tokenCache.expiration && now < tokenCache.expiration - 300000) {
        config.headers.Authorization = `Bearer ${tokenCache.token}`;
      } else {
        // Get a new token
        const token = await user.getIdToken(false);
        // Firebase tokens are valid for 1 hour
        tokenCache.token = token;
        tokenCache.expiration = now + 3600000; // Current time + 1 hour
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    // Only add /api/v1 prefix if URL doesn't already contain /api/
    if (!config.url.includes('/api/')) {
      config.url = `/api/v1${config.url}`;
    }
    return config;
  } catch (error) {
    console.error('Error in auth interceptor:', error);
    // Clear cache on error
    tokenCache.token = null;
    tokenCache.expiration = null;
    return Promise.reject(error);
  }
});

// Add response interceptor to handle CORS preflight
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      // Handle unauthorized error
      console.log('Unauthorized request:', error);
    }
    return Promise.reject(error);
  }
);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isGmailAuthorized, setIsGmailAuthorized] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [connectedEmails, setConnectedEmails] = useState([]);
  const [userPreferences, setUserPreferences] = useState(null);
  const [whatsAppNumber, setWhatsAppNumber] = useState(null);

  // Development bypass for proxy URL
  useEffect(() => {
    if (isDevelopmentProxy) {
      console.log('Development proxy detected - bypassing authentication');
      // Create a mock user for development purposes
      const mockUser = {
        uid: 'dev-user-123',
        email: 'dev@example.com',
        displayName: 'Development User',
        getIdToken: async () => 'mock-token-for-development'
      };
      setCurrentUser(mockUser);
      setLoading(false);
      return;
    }
  }, []);

  // Set up auth state listener first, before any conditional returns
  useEffect(() => {
    // Skip if we're using the development bypass
    if (isDevelopmentProxy) return;

    let mounted = true;

    // Check for redirect result first
    getRedirectResult(auth).then(async (result) => {
      if (result && mounted) {
        console.log('Redirect result received:', result.user.email);
        // The signed-in user info is in result.user
        setCurrentUser(result.user);
        setLoading(false);
      }
    }).catch((error) => {
      if (mounted) {
        console.error('Redirect result error:', error);
        setError(error.message);
        setLoading(false);
      }
    });

    const unsubscribe = onAuthStateChanged(
      auth,
      async (user) => {
        if (!mounted) return;

        console.log('Auth state changed:', user ? 'User logged in' : 'No user');
        
        if (user) {
          try {
            // Get user document
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            
            if (!userDoc.exists()) {
              // Create user document if it doesn't exist
              await setDoc(doc(db, 'users', user.uid), {
                email: user.email,
                created_at: serverTimestamp(),
                last_login: serverTimestamp()
              });
            } else {
              // Update last login
              await updateDoc(doc(db, 'users', user.uid), {
                last_login: serverTimestamp()
              });
            }
          } catch (error) {
            console.error('Error managing user document:', error);
            if (mounted) {
              setError(error.message);
            }
          }
        }

        if (mounted) {
          setCurrentUser(user);
          setLoading(false);
        }
      },
      (error) => {
        if (!mounted) return;
        
        console.error('Auth state change error:', error);
        setError(error.message);
        setLoading(false);
      }
    );

    // Cleanup subscription and mounted flag
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  // Set up Firebase listeners for user data, invoices, and email connections
  useEffect(() => {
    if (!currentUser) {
      setIsGmailAuthorized(false);
      setInvoices([]);
      setConnectedEmails([]);
      setUserPreferences(null);
      setWhatsAppNumber(null);
      return;
    }

    // Listen for user preferences
    const userDocRef = doc(db, 'users', currentUser.uid);
    const unsubscribeUserPreferences = onSnapshot(userDocRef, (snapshot) => {
      if (snapshot.exists()) {
        const userData = snapshot.data();
        setUserPreferences(userData);
        
        // Set WhatsApp number if it exists in the user data
        // The backend stores it as whatsapp_number, not phone
        if (userData.whatsapp_number) {
          setWhatsAppNumber(userData.whatsapp_number);
        } else {
          setWhatsAppNumber(null);
        }
      } else {
        setUserPreferences(null);
        setWhatsAppNumber(null);
      }
    });

    // Listen for email connections
    const emailConnectionsRef = collection(db, 'users', currentUser.uid, 'email_accounts');
    const unsubscribeEmails = onSnapshot(emailConnectionsRef, (snapshot) => {
      const emails = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        // Only include emails that have completed OAuth2
        if (data.email && (!data.oauth2_status || data.oauth2_status === 'authorized')) {
          emails.push(data.email);
        }
      });
      setConnectedEmails(emails);
      setIsGmailAuthorized(emails.length > 0);
    });

    // Listen for invoices collection changes
    const invoicesRef = collection(db, 'users', currentUser.uid, 'invoices');
    const q = query(
      invoicesRef,
      orderBy('date_of_issue', 'desc')
    );
    const unsubscribeInvoices = onSnapshot(q, (snapshot) => {
      const invoiceList = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(invoice => invoice.display_status !== false);
      setInvoices(invoiceList);
    });

    // Cleanup listeners
    return () => {
      unsubscribeEmails();
      unsubscribeInvoices();
      unsubscribeUserPreferences();
    };
  }, [currentUser]);

  // Sign up function
  async function signup(email, password) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      throw new Error(error.message || 'Failed to create account');
    }
  }

  // Login function
  async function login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      throw new Error(error.message || 'Failed to sign in');
    }
  }

  // Google Sign In function using popup
  async function signInWithGoogle() {
    try {
      // Set persistence to LOCAL to persist the auth state
      await setPersistence(auth, browserLocalPersistence);
      
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      const result = await signInWithPopup(auth, provider);
      
      // Update the current user state immediately
      setCurrentUser(result.user);
      
      // Create/update user document
      try {
        const userDoc = await getDoc(doc(db, 'users', result.user.uid));
        if (!userDoc.exists()) {
          await setDoc(doc(db, 'users', result.user.uid), {
            email: result.user.email,
            created_at: serverTimestamp(),
            last_login: serverTimestamp()
          });
        } else {
          await updateDoc(doc(db, 'users', result.user.uid), {
            last_login: serverTimestamp()
          });
        }
      } catch (error) {
        console.error('Error managing user document:', error);
      }
      
      return result.user;
    } catch (error) {
      console.error('Google Sign In Error:', error);
      throw new Error(error.message || 'Failed to sign in with Google');
    }
  }

  // Logout function
  async function logout() {
    try {
      await signOut(auth);
      setIsGmailAuthorized(false);
      setInvoices([]);
      setConnectedEmails([]);
    } catch (error) {
      throw new Error(error.message || 'Failed to log out');
    }
  }

  // Password reset function
  async function resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw new Error(error.message || 'Failed to reset password');
    }
  }

  const startGmailAuth = async (email) => {
    try {
      if (!currentUser) {
        throw new Error('No user logged in');
      }

      // Use the API instance for the auth request
      const response = await api.get(`/auth?user_id=${currentUser.uid}&email=${encodeURIComponent(email)}`);
      
      // Redirect to the auth URL from the response
      window.location.href = response.data.authUrl;
    } catch (error) {
      console.error('Error starting Gmail auth:', error);
      throw new Error(error.message || 'Failed to start Gmail authentication');
    }
  };

  const scanEmails = async () => {
    try {
      if (!currentUser) throw new Error('No user logged in');
      
      const response = await api.post(`/api/scan/${currentUser.uid}`);
      return response.data;
    } catch (error) {
      console.error('Error scanning emails:', error);
      throw new Error(error.message || 'Failed to scan emails');
    }
  };

  const deleteConnectedEmail = async (email) => {
    try {
      if (!currentUser) throw new Error('No user logged in');
      
      await api.delete(`/email/emails/${currentUser.uid}/${encodeURIComponent(email)}`);
      
      // Also delete from Firestore
      const emailRef = doc(db, 'users', currentUser.uid, 'email_accounts', email);
      await deleteDoc(emailRef);
      
      return true;
    } catch (error) {
      console.error('Error deleting email:', error);
      throw new Error(error.message || 'Failed to delete email connection');
    }
  };

  const updateInvoice = async (updatedInvoice, isNavigation = false) => {
    if (!currentUser) throw new Error('No user logged in');
    
    try {
      console.log('Updating invoice with data:', JSON.stringify(updatedInvoice, null, 2));
      
      // Set updated_at to 'navigation' if this is a navigation update
      const invoiceData = {
        ...updatedInvoice,
        updated_at: isNavigation ? 'navigation' : new Date().toISOString()
      };

      // Ensure we have a valid invoice ID
      const invoiceId = updatedInvoice.id || updatedInvoice._id;
      if (!invoiceId) {
        throw new Error('Invalid invoice ID');
      }

      const response = await api.put(
        `/users/${currentUser.uid}/invoices/${invoiceId}`,
        invoiceData
      );

      if (!response.data || response.status !== 200) {
        throw new Error(response.data?.error || 'Failed to update invoice');
      }

      return response.data;

    } catch (error) {
      console.error('Error updating invoice:', error);
      if (error.response?.data?.error) {
        throw new Error(`Failed to update invoice: ${error.response.data.error}`);
      }
      throw new Error(`Failed to update invoice: ${error.message}`);
    }
  };

  // Add connected email to Firestore
  async function addConnectedEmail(email) {
    try {
      if (!currentUser) {
        console.error('No user logged in when trying to add email');
        throw new Error('No user logged in');
      }
      
      console.log('Adding email to Firestore:', {
        userId: currentUser.uid,
        email: email
      });

      // Create user's email_accounts collection if it doesn't exist
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        await setDoc(userRef, {
          email: currentUser.email,
          created_at: serverTimestamp()
        });
      }

      // Add the email account
      const emailRef = doc(db, 'users', currentUser.uid, 'email_accounts', email);
      await setDoc(emailRef, {
        email: email,
        connected_at: serverTimestamp()
      });

      console.log('Email successfully added to Firestore');
      return true;
    } catch (error) {
      console.error('Failed to add email to Firestore:', error);
      throw new Error(error.message || 'Failed to add email connection');
    }
  }

  // Remove connected email
  async function removeConnectedEmail(email) {
    try {
      if (!currentUser) {
        console.error('No user logged in when trying to remove email');
        throw new Error('No user logged in');
      }

      console.log('Removing email connection:', {
        userId: currentUser.uid,
        email: email
      });
      
      // Call backend to remove email connection
      await api.delete(`/user/email/${currentUser.uid}/${encodeURIComponent(email)}`);

      // Remove from Firestore
      const emailRef = doc(db, 'users', currentUser.uid, 'email_accounts', email);
      await deleteDoc(emailRef);

      console.log('Email successfully removed from Firestore');
      return true;
    } catch (error) {
      console.error('Failed to remove email:', error);
      throw error;
    }
  };

  const checkGmailAuth = async () => {
    try {
      if (!currentUser) return false;
      
      const response = await api.get(`/auth/gmail-auth-status/${currentUser.uid}`);
      setIsGmailAuthorized(response.data.isAuthorized);
      return response.data.isAuthorized;
    } catch (error) {
      console.error('Error checking Gmail auth status:', error);
      setIsGmailAuthorized(false);
      return false;
    }
  };

  // Wrap fetchInvoices in useCallback
  const fetchInvoices = useCallback(async () => {
    if (!currentUser) return; // Return early if no user
    // setLoading(true); // Consider if loading state is needed here or handled elsewhere
    try {
      const invoicesRef = collection(db, 'users', currentUser.uid, 'invoices');
      const q = query(invoicesRef, orderBy('date_of_issue', 'desc'));
      const snapshot = await getDocs(q);
      
      const invoicesList = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(invoice => invoice.display_status !== false); // Filter out deleted invoices
      
      setInvoices(invoicesList); // Update state
      return invoicesList;
    } catch (error) {
      console.error('Error fetching invoices:', error);
      // setInvoices([]); // Optionally clear invoices on error
      throw new Error('Failed to fetch invoices');
    } finally {
      // setLoading(false); // Ensure loading is set false
    }
  }, [currentUser]); // Dependency: currentUser

  // Wrap deleteInvoice in useCallback
  const deleteInvoice = useCallback(async (invoiceId) => {
    if (!currentUser) throw new Error('No user logged in');
    try {
      // Delete from Firestore
      const invoiceRef = doc(db, 'users', currentUser.uid, 'invoices', invoiceId);
      await deleteDoc(invoiceRef);
      
      // Let the Firestore listener handle the state update
      // setInvoices(prev => prev.filter(invoice => invoice.id !== invoiceId));
      
      return true;
    } catch (error) {
      console.error('Error deleting invoice:', error);
      throw new Error('Failed to delete invoice');
    }
  }, [currentUser]); // Dependency: currentUser

  const setupFirestoreListeners = async (user) => {
    try {
      // Ensure we have a fresh token before setting up listeners
      const token = await user.getIdToken(true);
      
      // Set up your Firestore listeners here
      const invoicesQuery = query(
        collection(db, 'users', user.uid, 'invoices'),
        orderBy('created_at', 'desc')
      );

      const unsubscribe = onSnapshot(
        invoicesQuery,
        (snapshot) => {
          const invoicesList = [];
          snapshot.forEach((doc) => {
            invoicesList.push({ id: doc.id, ...doc.data() });
          });
          setInvoices(invoicesList);
        },
        (error) => {
          console.error('Firestore listener error:', error);
          if (error.code === 'permission-denied') {
            // Handle permission errors
            setError('Access denied. Please check your permissions.');
          } else if (error.code === 'unavailable') {
            // Handle offline/network errors
            setError('Network error. Retrying connection...');
            // Attempt to reconnect after a delay
            setTimeout(() => setupFirestoreListeners(user), 5000);
          }
        }
      );

      return unsubscribe;
    } catch (error) {
      console.error('Error setting up Firestore listeners:', error);
      setError('Error connecting to the database. Please refresh the page.');
    }
  };

  // Memoize the context value
  const value = useMemo(() => ({
    currentUser,
    api, // api instance is stable
    login, // Assuming these auth functions are stable (defined outside or wrapped in useCallback if needed)
    signup,
    logout,
    resetPassword,
    signInWithGoogle,
    loading,
    error,
    isGmailAuthorized,
    setIsGmailAuthorized, // State setter is stable
    invoices,
    setInvoices, // State setter is stable
    connectedEmails,
    setConnectedEmails, // State setter is stable
    fetchInvoices, // Now wrapped in useCallback
    deleteInvoice, // Now wrapped in useCallback
    updateInvoice, // Assuming stable or wrap in useCallback if defined inside
    addConnectedEmail, // Assuming stable or wrap in useCallback if defined inside
    removeConnectedEmail, // Assuming stable or wrap in useCallback if defined inside
    startGmailAuth, // Assuming stable or wrap in useCallback if defined inside
    scanEmails, // Assuming stable or wrap in useCallback if defined inside
    checkGmailAuth, // Assuming stable or wrap in useCallback if defined inside
    userPreferences,
    whatsAppNumber,
    setWhatsAppNumber
  }), [
    currentUser,
    loading,
    error,
    isGmailAuthorized,
    invoices,
    connectedEmails,
    fetchInvoices,
    deleteInvoice,
    userPreferences,
    whatsAppNumber
    // Assuming other functions like login, signup, updateInvoice etc. are stable
    // State setters like setIsGmailAuthorized, setInvoices are stable by default
  ]);

  // Render loading state
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        Loading authentication...
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
